import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { useTheme } from '../theme/ThemeProvider';
import { displayFont, monoFont } from '../theme/tokens';
import { Signal, Zap, TrendingUp, ArrowRight, Shield, Clock, BarChart3 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FallingStarLogo from '../theme/FallingStarLogo';
import { FadeUp, FadeRight, BlurIn, ScaleIn, StaggerContainer, StaggerItem } from '../components/ScrollReveal';

/* Counter that animates from 0 to target when visible */
function CountUp({ value, prefix = '', suffix = '', decimals = 0, duration = 1200, color }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // The terminal sequence is intentionally deterministic and loops on phase only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(value * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else setCount(value);
    };
    requestAnimationFrame(tick);
  }, [started, value, duration]);

  const formatted = count.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return <span ref={ref} style={{ color }}>{prefix}{formatted}{suffix}</span>;
}

/* WebGL mesh gradient shader (Stripe-style flowing abstract gradient) */
const VERT = `varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position, 1.0); }`;
const FRAG = `
precision highp float;
uniform float uTime;
uniform vec2 uRes;
uniform float uAlpha;
varying vec2 vUv;

vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
vec2 mod289(vec2 x){return x-floor(x*(1./289.))*289.;}
vec3 permute(vec3 x){return mod289(((x*34.)+1.)*x);}

float snoise(vec2 v){
  const vec4 C=vec4(.211324865405187,.366025403784439,-.577350269189626,.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.,0.):vec2(0.,1.);
  vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod289(i);
  vec3 p=permute(permute(i.y+vec3(0.,i1.y,1.))+i.x+vec3(0.,i1.x,1.));
  vec3 m=max(.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.);
  m=m*m;m=m*m;
  vec3 x=2.*fract(p*C.www)-1.;vec3 h=abs(x)-.5;
  vec3 ox=floor(x+.5);vec3 a0=x-ox;
  m*=1.79284291400159-.85373472095314*(a0*a0+h*h);
  vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.*dot(m,g);
}

float ribbon(vec2 p,float freq,float amp,float phase,float w,float t){
  float path=amp*sin(p.x*freq+phase+t);
  path+=snoise(vec2(p.x*.4+phase*.5,t*.3))*amp*.6;
  return smoothstep(w,w*.08,abs(p.y-path));
}

void main(){
  vec2 uv=vUv;
  float aspect=uRes.x/uRes.y;
  vec2 p=(uv-.5)*vec2(aspect,1.);
  float t=uTime*.12;

  float a=t*.15;
  float ca=cos(a),sa=sin(a);
  vec2 rp=vec2(p.x*ca-p.y*sa,p.x*sa+p.y*ca);

  float r1=ribbon(rp,2.,.4,0.,.28,t);
  float r2=ribbon(rp,1.6,.35,2.1,.24,t*1.2);
  float r3=ribbon(rp,2.3,.32,4.2,.2,t*.9);
  float r4=ribbon(rp,1.8,.42,6.3,.26,t*1.1);
  float r5=ribbon(rp,2.5,.28,8.,.18,t*.7);

  vec3 c1=vec3(.56,.72,1.);
  vec3 c2=vec3(.49,.98,.78);
  vec3 c3=vec3(.95,.79,.47);
  vec3 c4=vec3(.42,.64,1.);
  vec3 c5=vec3(.95,.98,1.);

  vec3 col=vec3(0.);
  col=mix(col,c1,r1);
  col=mix(col,c2,r2);
  col=mix(col,c3,r3);
  col=mix(col,c4,r4);
  col=mix(col,c5,r5);

  float alpha=max(max(max(r1,r2),max(r3,r4)),r5);

  float fade=smoothstep(0.,.12,vUv.x)*smoothstep(1.,.85,vUv.x);
  fade*=smoothstep(0.,.08,vUv.y)*smoothstep(1.,.55,vUv.y);

  gl_FragColor=vec4(col,alpha*fade*uAlpha);
}
`;

function HeroBackground({ isDark }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uRes: { value: new THREE.Vector2(mount.clientWidth, mount.clientHeight) },
        uAlpha: { value: isDark ? 0.6 : 0.7 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
    });
    scene.add(new THREE.Mesh(geo, mat));

    const clock = new THREE.Clock();
    let frameId;
    const animate = () => {
      mat.uniforms.uTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };

    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w, h);
      mat.uniforms.uRes.value.set(w, h);
    };
    window.addEventListener('resize', onResize);
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      geo.dispose();
      mat.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, [isDark]);

  const gridPaths = React.useMemo(() => {
    const ps = [];
    const W = 1400, H = 800, gap = 50, amp = 8, freq = 0.015;
    for (let y = 0; y <= H; y += gap) {
      let d = '';
      for (let x = 0; x <= W; x += 8) {
        d += `${x === 0 ? 'M' : 'L'}${x},${(y + Math.sin(x * freq + y * 0.005) * amp).toFixed(1)} `;
      }
      ps.push(d);
    }
    for (let x = 0; x <= W; x += gap) {
      let d = '';
      for (let y = 0; y <= H; y += 8) {
        d += `${y === 0 ? 'M' : 'L'}${(x + Math.sin(y * freq + x * 0.005) * amp).toFixed(1)},${y} `;
      }
      ps.push(d);
    }
    return ps;
  }, []);

  return (
    <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '100vw', height: '125%', overflow: 'hidden', pointerEvents: 'none' }}>
      <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />
      <svg width="100%" height="100%" viewBox="0 0 1400 800" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <radialGradient id="wgFade" cx="50%" cy="40%" r="55%">
            <stop offset="0%" stopColor="white" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="wgMask">
            <rect width="100%" height="100%" fill="url(#wgFade)" />
          </mask>
        </defs>
        <g mask="url(#wgMask)" opacity={isDark ? 0.06 : 0.035}>
          {gridPaths.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={isDark ? '#8fb7ff' : '#6aa3ff'} strokeWidth="0.5" />
          ))}
        </g>
      </svg>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: `linear-gradient(to bottom, transparent, ${isDark ? '#060918' : '#f0f2f7'} 85%)` }} />
    </div>
  );
}

const MOCK_TERMINAL_SCRIPT = [
  { text: '$ Listening on @GoldSignalsPro...', color: '#94a3b8', delay: 800 },
  { text: '', delay: 400 },
  { text: '  New signal detected', color: '#8fb7ff', delay: 600 },
  { text: '  BUY XAUUSD @ 2341.50', color: '#fbbf24', delay: 80 },
  { text: '  SL: 2338.00  TP1: 2345.00  TP2: 2350.00', color: '#94a3b8', delay: 80 },
  { text: '', delay: 300 },
  { text: '  Validating price... current: 2341.62', color: '#6aa3ff', delay: 600 },
  { text: '  Margin check passed', color: '#9ae7ff', delay: 400 },
  { text: '  Placing 2 orders (0.02 lots each)', color: '#6aa3ff', delay: 500 },
  { text: '', delay: 200 },
  { text: '  TP1 order filled  #48291034', color: '#7df9c6', delay: 300 },
  { text: '  TP2 order filled  #48291035', color: '#7df9c6', delay: 300 },
  { text: '', delay: 600 },
  { text: '$ Waiting for next signal...', color: '#94a3b8', delay: 1500 },
  { text: '', delay: 400 },
  { text: '  New signal detected', color: '#8fb7ff', delay: 600 },
  { text: '  SELL GBPUSD @ 1.2715', color: '#fb7185', delay: 80 },
  { text: '  SL: 1.2745  TP: 1.2680', color: '#94a3b8', delay: 80 },
  { text: '', delay: 300 },
  { text: '  Validating price... current: 1.2713', color: '#6aa3ff', delay: 600 },
  { text: '  Order filled  #48291041', color: '#7df9c6', delay: 400 },
  { text: '', delay: 800 },
  { text: '  TP1 hit on XAUUSD  +$7.00', color: '#7df9c6', delay: 1000 },
  { text: '', delay: 500 },
  { text: '$ Waiting for next signal...', color: '#94a3b8', delay: 2000 },
];

/* Live mock terminal */
function MockTerminal({ isDark }) {
  const [lines, setLines] = useState([]);
  const [phase, setPhase] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (phase >= MOCK_TERMINAL_SCRIPT.length) {
      const t = setTimeout(() => { setLines([]); setPhase(0); }, 2500);
      return () => clearTimeout(t);
    }

    const entry = MOCK_TERMINAL_SCRIPT[phase];
    const t = setTimeout(() => {
      if (entry.text !== '') {
        setLines(prev => [...prev].slice(-12).concat({ text: entry.text, color: entry.color }));
      } else {
        setLines(prev => [...prev].slice(-12).concat({ text: '', color: '' }));
      }
      setPhase(p => p + 1);
    }, entry.delay);

    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines]);

  const dotColors = ['#ff5f57', '#febc2e', '#28c840'];

  return (
    <div
      className="prism-panel iri-border"
      style={{
        position: 'relative',
        background: isDark
          ? 'linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 42%, rgba(255,255,255,0.01) 100%), linear-gradient(135deg, rgba(143,183,255,0.14) 0%, rgba(125,249,198,0.08) 52%, rgba(242,201,120,0.10) 100%), rgba(8,10,14,0.76)'
          : 'linear-gradient(160deg, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.55) 46%, rgba(255,255,255,0.48) 100%), linear-gradient(135deg, rgba(143,183,255,0.18) 0%, rgba(125,249,198,0.10) 52%, rgba(242,201,120,0.12) 100%)',
        backdropFilter: 'blur(24px) saturate(1.3)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
        borderRadius: 20,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.42)'}`,
        overflow: 'hidden',
        fontFamily: monoFont,
        boxShadow: isDark
          ? '0 28px 70px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.06)'
          : '0 24px 60px rgba(141,152,168,0.18), inset 0 1px 0 rgba(255,255,255,0.42)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'linear-gradient(100deg, rgba(248,251,255,0.02) 10%, rgba(143,183,255,0.10) 28%, rgba(125,249,198,0.04) 54%, transparent 72%)',
          opacity: isDark ? 1 : 0.8,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 14,
          bottom: 14,
          right: 10,
          width: 4,
          borderRadius: 999,
          pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(248,251,255,0.10) 0%, rgba(143,183,255,0.85) 22%, rgba(125,249,198,0.78) 54%, rgba(242,201,120,0.75) 82%, rgba(248,251,255,0.14) 100%)',
          boxShadow: '0 0 18px rgba(143,183,255,0.35)',
          opacity: isDark ? 0.85 : 0.7,
        }}
      />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)'}`,
        background: isDark
          ? 'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))'
          : 'linear-gradient(90deg, rgba(255,255,255,0.44), rgba(255,255,255,0.18))',
      }}>
        {dotColors.map((c, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
        ))}
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 8, fontWeight: 500 }}>xceller. terminal</span>
      </div>
      <div ref={containerRef} style={{ padding: '14px 16px', height: 340, overflowY: 'auto', scrollbarWidth: 'none' }}>
        {lines.map((line, i) => (
          <div key={i} style={{
            fontSize: 12.5, lineHeight: 1.7, color: line.color || 'rgba(255,255,255,0.5)',
            minHeight: line.text === '' ? 10 : 'auto',
            animation: 'fadeIn 0.15s ease',
            whiteSpace: 'pre',
          }}>
            {line.text}
            {i === lines.length - 1 && line.text && (
              <span style={{
                display: 'inline-block', width: 7, height: 14, background: '#8fb7ff',
                marginLeft: 2, verticalAlign: 'middle', animation: 'blink 1s step-end infinite',
              }} />
            )}
          </div>
        ))}
        {lines.length === 0 && (
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12.5 }}>
            <span style={{
              display: 'inline-block', width: 7, height: 14, background: '#8fb7ff',
              verticalAlign: 'middle', animation: 'blink 1s step-end infinite',
            }} />
          </span>
        )}
      </div>
    </div>
  );
}

/* Orb that travels between the three step nodes */
function TravellingOrb({ nodeRefs }) {
  const orbRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    let frameId;
    let startTime = null;
    const DURATION = 3800;
    const PAUSE = 600;

    const getWaypoints = () => {
      const line = lineRef.current;
      if (!line) return null;
      const lineRect = line.getBoundingClientRect();
      return nodeRefs.map(ref => {
        if (!ref.current) return 0;
        const r = ref.current.getBoundingClientRect();
        return (r.top + r.height / 2) - lineRect.top;
      });
    };

    const SEGMENT_TRAVEL = (DURATION - PAUSE * 3) / 2;
    const total = PAUSE + SEGMENT_TRAVEL + PAUSE + SEGMENT_TRAVEL + PAUSE;

    const colors = [
      [143, 183, 255],
      [242, 201, 120],
      [125, 249, 198],
    ];

    const lerp = (a, b, t) => a + (b - a) * t;
    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const tick = (ts) => {
      if (!startTime) startTime = ts;
      const elapsed = (ts - startTime) % total;
      const orb = orbRef.current;
      const wps = getWaypoints();
      if (!orb || !wps) {
        frameId = requestAnimationFrame(tick);
        return;
      }

      let pos;
      let colorIdx;
      let opacity = 1;

      if (elapsed < PAUSE) {
        pos = wps[0];
        colorIdx = 0;

        const fadeT = clamp(elapsed / (PAUSE * 0.7), 0, 1);
        opacity = fadeT;
      } else if (elapsed < PAUSE + SEGMENT_TRAVEL) {
        const t = ease((elapsed - PAUSE) / SEGMENT_TRAVEL);
        pos = lerp(wps[0], wps[1], t);
        colorIdx = t;
        opacity = 1;
      } else if (elapsed < PAUSE + SEGMENT_TRAVEL + PAUSE) {
        pos = wps[1];
        colorIdx = 1;
        opacity = 1;
      } else if (elapsed < PAUSE + SEGMENT_TRAVEL + PAUSE + SEGMENT_TRAVEL) {
        const t = ease((elapsed - PAUSE - SEGMENT_TRAVEL - PAUSE) / SEGMENT_TRAVEL);
        pos = lerp(wps[1], wps[2], t);
        colorIdx = 1 + t;
        opacity = 1;
      } else {
        pos = wps[2];
        colorIdx = 2;

        const fadeOutElapsed = elapsed - (PAUSE + SEGMENT_TRAVEL + PAUSE + SEGMENT_TRAVEL);
        const fadeT = clamp(fadeOutElapsed / (PAUSE * 0.85), 0, 1);
        opacity = 1 - fadeT;
      }

      const ci = Math.min(colorIdx, 1.999);
      const ci0 = Math.floor(ci);
      const ci1 = Math.min(ci0 + 1, 2);
      const ct = ci - ci0;

      const r = Math.round(lerp(colors[ci0][0], colors[ci1][0], ct));
      const g = Math.round(lerp(colors[ci0][1], colors[ci1][1], ct));
      const b = Math.round(lerp(colors[ci0][2], colors[ci1][2], ct));

      orb.style.top = `${pos}px`;
      orb.style.opacity = `${opacity}`;
      orb.style.background = `rgb(${r},${g},${b})`;
      orb.style.boxShadow = `0 0 14px 6px rgba(${r},${g},${b},${0.75 * opacity}), 0 0 32px 12px rgba(${r},${g},${b},${0.3 * opacity})`;

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [nodeRefs]);

  return (
    <div
      ref={lineRef}
      style={{
        position: 'absolute',
        left: '50%',
        top: 0,
        bottom: 0,
        width: 1,
        transform: 'translateX(-50%)',
        background: 'linear-gradient(to bottom, rgba(143,183,255,0.35), rgba(242,201,120,0.32), rgba(125,249,198,0.35))',
        pointerEvents: 'none'
      }}
    >
      <div
        ref={orbRef}
        style={{
          position: 'absolute',
          left: '50%',
          width: 16,
          height: 16,
          marginLeft: -8,
          marginTop: -8,
          borderRadius: '50%',
          pointerEvents: 'none',
          filter: 'blur(0.5px)',
          willChange: 'top, background, box-shadow, opacity',
          opacity: 0,
        }}
      />
    </div>
  );
}

function TiltCard({ children }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ transform: 'rotateY(-8deg) rotateX(2deg)' });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `rotateY(${x * 20}deg) rotateX(${-y * 15}deg) scale(1.02)`,
    });
  };

  const handleLeave = () => {
    setStyle({ transform: 'rotateY(-8deg) rotateX(2deg)' });
  };

  return (
    <div style={{ perspective: 1000 }}>
      <div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ ...style, transformStyle: 'preserve-3d', transition: 'transform 0.15s ease-out', willChange: 'transform' }}
      >
        {children}
      </div>
    </div>
  );
}

/* Large horizontal glowing spline with wind float effect */
// eslint-disable-next-line no-unused-vars
function SplineReveal() {
  const ref = useRef(null);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTriggered(true); },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const windStyle = (name, duration, delay = '0s') => ({
    animation: triggered ? `${name} ${duration} ease-in-out infinite ${delay}` : 'none',
    transition: 'opacity 0.6s ease',
  });

  return (
    <>
      <style>{`
        @keyframes wf1 { 0%,100%{transform:translateY(0)} 35%{transform:translateY(-22px)} 70%{transform:translateY(10px)} }
        @keyframes wf2 { 0%,100%{transform:translateY(0)} 25%{transform:translateY(18px)} 65%{transform:translateY(-26px)} }
        @keyframes wf3 { 0%,100%{transform:translateY(0)} 45%{transform:translateY(-16px)} 80%{transform:translateY(20px)} }
        @keyframes wf4 { 0%,100%{transform:translateY(0)} 20%{transform:translateY(24px)} 55%{transform:translateY(-14px)} }
        @keyframes wf5 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-28px)} 85%{transform:translateY(8px)} }
        @keyframes wf6 { 0%,100%{transform:translateY(0)} 30%{transform:translateY(16px)} 70%{transform:translateY(-20px)} }
        @keyframes dotPulse { 0%,100%{r:3;opacity:0.9} 50%{r:5;opacity:0.5} }

        @media (max-width: 768px) {
          .spline-reveal-wrap { height: 200px !important; }
        }
        @media (max-width: 480px) {
          .spline-reveal-wrap { height: 140px !important; }
        }
      `}</style>
      <div
        ref={ref}
        className="spline-reveal-wrap"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: 420,
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
        }}
      >
        <svg
          viewBox="0 0 1280 420"
          fill="none"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            width: '100%',
            height: '100%',
            transform: triggered ? 'translateX(0)' : 'translateX(105%)',
            opacity: triggered ? 1 : 0,
            transition: `transform 1.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease-out`,
          }}
        >
          <defs>
            <linearGradient id="hsg1" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#f8fbff" stopOpacity="0" />
              <stop offset="14%"  stopColor="#8fb7ff" />
              <stop offset="48%"  stopColor="#7df9c6" />
              <stop offset="82%"  stopColor="#f2c978" />
              <stop offset="100%" stopColor="#6aa3ff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="hsg2" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#f8fbff" stopOpacity="0" />
              <stop offset="18%"  stopColor="#9ae7ff" />
              <stop offset="55%"  stopColor="#6aa3ff" />
              <stop offset="100%" stopColor="#6aa3ff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="hsg3" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#f8fbff" stopOpacity="0" />
              <stop offset="22%"  stopColor="#f2c978" />
              <stop offset="68%"  stopColor="#7df9c6" />
              <stop offset="100%" stopColor="#7df9c6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="hsg4" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#f8fbff" stopOpacity="0" />
              <stop offset="28%"  stopColor="#8fb7ff" />
              <stop offset="72%"  stopColor="#9ae7ff" />
              <stop offset="100%" stopColor="#9ae7ff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="hsg5" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#8fb7ff" stopOpacity="0" />
              <stop offset="35%"  stopColor="#8fb7ff" stopOpacity="0.6" />
              <stop offset="65%"  stopColor="#7df9c6" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#7df9c6" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="hsg6" x1="1280" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%"   stopColor="#7df9c6" stopOpacity="0" />
              <stop offset="30%"  stopColor="#7df9c6" stopOpacity="0.5" />
              <stop offset="70%"  stopColor="#f2c978" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#f2c978" stopOpacity="0" />
            </linearGradient>

            <filter id="glow-heavy" x="-20%" y="-500%" width="140%" height="1100%">
              <feGaussianBlur stdDeviation="22" result="b1"/>
              <feGaussianBlur stdDeviation="10" result="b2" in="SourceGraphic"/>
              <feMerge><feMergeNode in="b1"/><feMergeNode in="b2"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="glow-mid" x="-20%" y="-400%" width="140%" height="900%">
              <feGaussianBlur stdDeviation="12" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="glow-soft" x="-20%" y="-300%" width="140%" height="700%">
              <feGaussianBlur stdDeviation="6"/>
            </filter>
            <filter id="glow-ambient" x="-30%" y="-600%" width="160%" height="1300%">
              <feGaussianBlur stdDeviation="40"/>
            </filter>
          </defs>

          {/* ── Curve 1 — top, wide S-wave ── */}
          <g style={windStyle('wf1', '8.5s', '0s')}>
            <path d="M 1280 70 C 1040 70, 860 170, 640 70 C 420 -30, 200 70, 0 70"
                  stroke="url(#hsg1)" strokeWidth="55" opacity="0.06" filter="url(#glow-ambient)" />
            <path d="M 1280 70 C 1040 70, 860 170, 640 70 C 420 -30, 200 70, 0 70"
                  stroke="url(#hsg1)" strokeWidth="22" opacity="0.15" filter="url(#glow-soft)" />
            <path d="M 1280 70 C 1040 70, 860 170, 640 70 C 420 -30, 200 70, 0 70"
                  stroke="url(#hsg1)" strokeWidth="2.5" opacity="0.95" filter="url(#glow-mid)" strokeLinecap="round" />
          </g>

          {/* ── Curve 2 — upper-mid, inverted phase ── */}
          <g style={windStyle('wf2', '11s', '1.2s')}>
            <path d="M 1280 140 C 1060 140, 820 40, 580 140 C 340 240, 120 140, 0 140"
                  stroke="url(#hsg2)" strokeWidth="40" opacity="0.07" filter="url(#glow-ambient)" />
            <path d="M 1280 140 C 1060 140, 820 40, 580 140 C 340 240, 120 140, 0 140"
                  stroke="url(#hsg2)" strokeWidth="16" opacity="0.16" filter="url(#glow-soft)" />
            <path d="M 1280 140 C 1060 140, 820 40, 580 140 C 340 240, 120 140, 0 140"
                  stroke="url(#hsg2)" strokeWidth="2" opacity="0.8" filter="url(#glow-mid)" strokeLinecap="round" />
          </g>

          {/* ── Curve 3 — center, main focal wave ── */}
          <g style={windStyle('wf3', '9.5s', '0.6s')}>
            <path d="M 1280 210 C 1000 210, 800 110, 640 210 C 480 310, 260 210, 0 210"
                  stroke="url(#hsg1)" strokeWidth="60" opacity="0.09" filter="url(#glow-ambient)" />
            <path d="M 1280 210 C 1000 210, 800 110, 640 210 C 480 310, 260 210, 0 210"
                  stroke="url(#hsg1)" strokeWidth="28" opacity="0.2" filter="url(#glow-soft)" />
            <path d="M 1280 210 C 1000 210, 800 110, 640 210 C 480 310, 260 210, 0 210"
                  stroke="url(#hsg1)" strokeWidth="3" opacity="1" filter="url(#glow-heavy)" strokeLinecap="round" />
            {/* thin echo */}
            <path d="M 1280 210 C 1000 210, 800 110, 640 210 C 480 310, 260 210, 0 210"
                  stroke="url(#hsg5)" strokeWidth="0.8" opacity="0.5" strokeLinecap="round" />
          </g>

          {/* ── Curve 4 — lower-mid ── */}
          <g style={windStyle('wf4', '12s', '2s')}>
            <path d="M 1280 280 C 1050 280, 790 370, 560 280 C 330 190, 140 280, 0 280"
                  stroke="url(#hsg3)" strokeWidth="40" opacity="0.07" filter="url(#glow-ambient)" />
            <path d="M 1280 280 C 1050 280, 790 370, 560 280 C 330 190, 140 280, 0 280"
                  stroke="url(#hsg3)" strokeWidth="16" opacity="0.15" filter="url(#glow-soft)" />
            <path d="M 1280 280 C 1050 280, 790 370, 560 280 C 330 190, 140 280, 0 280"
                  stroke="url(#hsg3)" strokeWidth="2" opacity="0.8" filter="url(#glow-mid)" strokeLinecap="round" />
          </g>

          {/* ── Curve 5 — bottom ── */}
          <g style={windStyle('wf5', '10s', '0.9s')}>
            <path d="M 1280 350 C 980 350, 760 255, 540 350 C 320 445, 120 350, 0 350"
                  stroke="url(#hsg4)" strokeWidth="45" opacity="0.06" filter="url(#glow-ambient)" />
            <path d="M 1280 350 C 980 350, 760 255, 540 350 C 320 445, 120 350, 0 350"
                  stroke="url(#hsg4)" strokeWidth="18" opacity="0.13" filter="url(#glow-soft)" />
            <path d="M 1280 350 C 980 350, 760 255, 540 350 C 320 445, 120 350, 0 350"
                  stroke="url(#hsg4)" strokeWidth="2" opacity="0.75" filter="url(#glow-mid)" strokeLinecap="round" />
          </g>

          {/* ── Hairline accent threads ── */}
          <g style={windStyle('wf6', '14s', '0.4s')} opacity="0.4">
            <path d="M 1280 175 C 1020 175, 820 78, 620 175 C 420 272, 200 175, 0 175"
                  stroke="url(#hsg5)" strokeWidth="0.8" strokeLinecap="round" />
            <path d="M 1280 245 C 1030 245, 800 335, 580 245 C 360 155, 160 245, 0 245"
                  stroke="url(#hsg6)" strokeWidth="0.8" strokeLinecap="round" />
          </g>

          {/* ── Glowing dots at wave nodes ── */}
          {[
            { cx: 640, cy: 70,  color: '#8fb7ff', delay: '0.9s', wave: 'wf1', dur: '8.5s' },
            { cx: 580, cy: 140, color: '#9ae7ff', delay: '1.0s', wave: 'wf2', dur: '11s'  },
            { cx: 640, cy: 210, color: '#6aa3ff', delay: '0.8s', wave: 'wf3', dur: '9.5s' },
            { cx: 300, cy: 210, color: '#7df9c6', delay: '1.1s', wave: 'wf3', dur: '9.5s' },
            { cx: 980, cy: 210, color: '#f2c978', delay: '1.0s', wave: 'wf3', dur: '9.5s' },
            { cx: 560, cy: 280, color: '#7df9c6', delay: '1.2s', wave: 'wf4', dur: '12s'  },
            { cx: 540, cy: 350, color: '#9ae7ff', delay: '1.1s', wave: 'wf5', dur: '10s'  },
          ].map((dot, i) => (
            <g key={i} style={{ animation: triggered ? `${dot.wave} ${dot.dur} ease-in-out infinite ${dot.delay}` : 'none' }}>
              <circle cx={dot.cx} cy={dot.cy} r={14} fill={dot.color}
                opacity={triggered ? 0.08 : 0}
                style={{ transition: `opacity 0.5s ease ${dot.delay}` }} />
              <circle cx={dot.cx} cy={dot.cy} r={6} fill={dot.color}
                opacity={triggered ? 0.2 : 0}
                style={{ transition: `opacity 0.5s ease ${dot.delay}` }} />
              <circle cx={dot.cx} cy={dot.cy} r={3} fill={dot.color}
                opacity={triggered ? 1 : 0}
                style={{ transition: `opacity 0.4s ease ${dot.delay}`, filter: 'url(#glow-mid)' }} />
            </g>
          ))}
        </svg>
      </div>
    </>
  );
}
export default function LandingPage() {
  const { t, isDark, iri } = useTheme();

  return (
    <>
      <Navbar />
      <style>{`
        @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.3; }
          50% { transform: translateY(10px); opacity: 1; }
        }
        @keyframes scrollDot {
          0% { transform: translateY(0); opacity: 1; }
          60% { transform: translateY(12px); opacity: 0; }
          61% { transform: translateY(0); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes diagShimmer {
          0%   { transform: translateX(-150%) skewX(-15deg); opacity: 0; }
          8%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { transform: translateX(250%) skewX(-15deg); opacity: 0; }
        }
        @keyframes diagShimmer2 {
          0%   { transform: translateX(-150%) skewX(-15deg); opacity: 0; }
          8%   { opacity: 0.5; }
          92%  { opacity: 0.5; }
          100% { transform: translateX(250%) skewX(-15deg); opacity: 0; }
        }
        @keyframes stepFadeLeft {
          from { opacity: 0; transform: translateX(-36px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes stepFadeRight {
          from { opacity: 0; transform: translateX(36px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes nodePopIn {
          from { opacity: 0; transform: scale(0.3); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes trustSlide {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes nodePulseRing {
          0%   { box-shadow: 0 0 0 0 currentColor; opacity: 0.6; }
          70%  { box-shadow: 0 0 0 10px transparent; opacity: 0; }
          100% { box-shadow: 0 0 0 0 transparent; opacity: 0; }
        }
        @keyframes ambientPulse {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.06); opacity: 1; }
        }
        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }
        @keyframes orbTravel { from {} to {} }

        .step-card-left, .step-card-right {
          transition:
            transform 0.35s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.35s cubic-bezier(0.22,1,0.36,1),
            border-color 0.35s ease,
            background 0.35s ease;
          cursor: default;
          will-change: transform, box-shadow;
        }
        .step-card-left:hover {
          transform: translateX(8px) translateY(-6px) scale(1.022) !important;
          box-shadow: 0 18px 50px rgba(111,168,255,0.22), 0 0 0 1px rgba(255,255,255,0.05) !important;
        }
        .step-card-right:hover {
          transform: translateX(-8px) translateY(-6px) scale(1.022) !important;
          box-shadow: 0 18px 50px rgba(125,249,198,0.18), 0 0 0 1px rgba(255,255,255,0.05) !important;
        }

        .stats-card {
          transition:
            transform 0.35s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.35s cubic-bezier(0.22,1,0.36,1),
            border-color 0.3s ease,
            background 0.3s ease;
          will-change: transform, box-shadow;
        }
        .stats-card:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 18px 40px rgba(111,168,255,0.16);
        }

        .trust-big-card {
          transition:
            transform 0.55s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.55s cubic-bezier(0.22,1,0.36,1),
            border-color 0.4s ease,
            background 0.4s ease;
          cursor: default;
          will-change: transform, box-shadow;
        }
        .trust-big-card:hover {
          transform: translateY(-10px) scale(1.025);
          box-shadow: 0 24px 55px rgba(111,168,255,0.20), 0 0 0 1px rgba(255,255,255,0.06);
        }

        .trust-bar-fill {
          transition: width 0.55s ease, box-shadow 0.35s ease, filter 0.35s ease;
        }
        .trust-big-card:hover .trust-bar-fill {
          filter: brightness(1.12);
          box-shadow: 0 0 14px currentColor;
        }

        .cta-card {
          transition:
            transform 0.45s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.45s cubic-bezier(0.22,1,0.36,1),
            border-color 0.35s ease;
          will-change: transform, box-shadow;
        }
        .cta-card:hover {
          transform: translateY(-8px) scale(1.01);
          box-shadow: 0 36px 90px rgba(0,0,0,0.34), 0 12px 48px rgba(111,168,255,0.14);
        }

        .cta-button {
          transition:
            transform 0.25s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.25s cubic-bezier(0.22,1,0.36,1),
            filter 0.25s ease;
        }
        .cta-button:hover {
          transform: translateY(-3px) scale(1.025);
          filter: brightness(1.04);
          box-shadow: 0 16px 36px rgba(111,168,255,0.28), 0 8px 24px rgba(125,249,198,0.12), inset 0 1px 0 rgba(255,255,255,0.15);
        }

        .hero-primary-button {
          transition:
            transform 0.25s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.25s cubic-bezier(0.22,1,0.36,1),
            filter 0.25s ease;
        }
        .hero-primary-button:hover {
          transform: translateY(-3px) scale(1.025);
          filter: brightness(1.03);
          box-shadow: 0 12px 30px rgba(111,168,255,0.32), 0 8px 18px rgba(125,249,198,0.12);
        }

        .hero-secondary-button {
          transition:
            transform 0.25s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.25s cubic-bezier(0.22,1,0.36,1),
            border-color 0.25s ease;
        }
        .hero-secondary-button:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 10px 24px rgba(111,168,255,0.12);
        }

        .node-shell {
          transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease;
        }
        .node-shell:hover {
          transform: scale(1.08);
        }

        .node-inner-ring {
          animation: ambientPulse 2.6s ease-in-out infinite;
        }

        /* ── Mobile Responsive ── */
        @media (max-width: 900px) {
          .grid-mobile-1 {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .hero-title { font-size: 38px !important; }
          .hero-sub   { font-size: 15px !important; }
        }
        @media (max-width: 768px) {
          .grid-mobile-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .trust-grid {
            grid-template-columns: 1fr !important;
          }
          .cta-inner-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
          .cta-inner-grid > div:last-child {
            align-items: flex-start !important;
          }
          .steps-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .step-center-col { display: none !important; }
          .section-title { font-size: 26px !important; }
          .hero-title     { font-size: 32px !important; }
        }
        @media (max-width: 520px) {
          .grid-mobile-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .hero-title { font-size: 28px !important; }
          .hero-sub   { font-size: 14px !important; }
        }
      `}</style>
      <div style={{ position: 'relative', zIndex: 1, overflowX: 'hidden' }}>

        {/* Hero */}
        <section style={{ position: 'relative', maxWidth: 1280, margin: '0 auto', padding: '60px 24px 40px', overflow: 'visible' }}>
          <HeroBackground isDark={isDark} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="grid-mobile-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center', minHeight: 420 }}>
              <div>
                <FadeUp delay={0.1}>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: t.sectionBg, border: `1px solid ${t.sectionBorder}`, borderRadius: 20, padding: '6px 16px 6px 10px' }}>
                      <FallingStarLogo size={20} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>Automated Signal Trading</span>
                    </div>
                  </div>
                </FadeUp>
                <BlurIn delay={0.2}>
                  <h1 className="iri-text hero-title" style={{ fontFamily: displayFont, fontSize: 52, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 18 }}>
                    Trade signals,<br />executed instantly.
                  </h1>
                </BlurIn>
                <FadeUp delay={0.35} y={20}>
                  <p className="hero-sub" style={{ fontSize: 17, color: t.textMuted, maxWidth: 460, lineHeight: 1.6, fontWeight: 400, marginBottom: 32 }}>
                    Connect your Telegram channels to MetaTrader 5. Signals get parsed, checked, and placed as trades automatically.
                  </p>
                </FadeUp>
                <FadeUp delay={0.45} y={20}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <Link
                      to="/login"
                      className="hero-primary-button"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 12,
                        background: iri, color: '#fff', fontSize: 15, fontWeight: 700,
                        border: 'none', textDecoration: 'none',
                        boxShadow: '0 16px 36px rgba(111,168,255,0.24)',
                      }}
                    >
                      Get Started <ArrowRight size={16} />
                    </Link>
                    <Link
                      to="/about"
                      className="hero-secondary-button"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 28px', borderRadius: 12,
                        background: t.primaryBg, border: `1px solid ${t.primaryBorder}`, color: t.primaryText,
                        fontSize: 15, fontWeight: 600, textDecoration: 'none',
                      }}
                    >
                      Learn More
                    </Link>
                  </div>
                </FadeUp>
              </div>

              <FadeRight delay={0.4} duration={0.8} x={120}>
                <TiltCard>
                  <MockTerminal isDark={isDark} />
                </TiltCard>
              </FadeRight>
            </div>

            <FadeUp delay={0.6} y={20}>
              <div className="grid-mobile-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 40 }}>
                {[
                  { label: 'Signals', value: 1247, decimals: 0, prefix: '', suffix: '', icon: Signal },
                  { label: 'Win Rate', value: 89.2, decimals: 1, prefix: '', suffix: '%', icon: TrendingUp },
                  { label: 'Avg Latency', value: 0.3, decimals: 1, prefix: '', suffix: 's', icon: Clock },
                  { label: 'Net P&L', value: 12450, decimals: 0, prefix: '+$', suffix: '', icon: BarChart3, color: t.positive },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="stats-card prism-panel-soft iri-border"
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      background: isDark
                        ? 'linear-gradient(155deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 44%, rgba(255,255,255,0.01) 100%), rgba(255,255,255,0.025)'
                        : 'linear-gradient(155deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.60) 44%, rgba(255,255,255,0.54) 100%)',
                      border: `1px solid ${t.sectionBorder}`,
                      borderRadius: 18,
                      padding: '18px 14px',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      bottom: 12,
                      right: 8,
                      width: 3,
                      borderRadius: 999,
                      background: 'linear-gradient(180deg, rgba(248,251,255,0.04) 0%, rgba(143,183,255,0.64) 25%, rgba(125,249,198,0.58) 62%, rgba(242,201,120,0.60) 100%)',
                      boxShadow: '0 0 16px rgba(143,183,255,0.24)',
                    }} />
                    <s.icon size={16} color={t.textFaint} style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: s.color || t.text }}>
                      <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} duration={1400 + i * 200} color={s.color} />
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textFaint, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </FadeUp>

            {/* Scroll indicator */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: 0.5 }}>
                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.textFaint }}>Scroll</span>
                <div style={{ width: 20, height: 32, borderRadius: 10, border: `1.5px solid ${t.textFaint}`, display: 'flex', justifyContent: 'center', paddingTop: 6 }}>
                  <div style={{ width: 3, height: 8, borderRadius: 2, background: t.textFaint, animation: 'scrollBounce 1.8s ease-in-out infinite' }} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section style={{ position: 'relative', maxWidth: 1280, margin: '0 auto', padding: '40px 24px 100px', overflow: 'visible' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: 100 }}>
              <div style={{ display: 'inline-block', background: 'rgba(143,183,255,0.10)', border: '1px solid rgba(143,183,255,0.24)', borderRadius: 20, padding: '4px 14px', marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8fb7ff' }}>How it works</span>
              </div>
              <h2 className="iri-text section-title" style={{ fontFamily: displayFont, fontSize: 36, fontWeight: 900, marginBottom: 10, letterSpacing: '-0.02em' }}>Signal in. Trade out.</h2>
              <p style={{ fontSize: 15, color: t.textMuted }}>Three steps. Zero manual effort.</p>
            </div>
          </FadeUp>

          {(() => {
            const nodeRef1 = React.createRef();
            const nodeRef2 = React.createRef();
            const nodeRef3 = React.createRef();
            const nodeRefs = [nodeRef1, nodeRef2, nodeRef3];

            const steps = [
              {
                num: '01',
                icon: Signal,
                title: 'Signal Detection',
                color: '#8fb7ff',
                glow: 'rgba(143,183,255,0.2)',
                desc: 'Monitors your Telegram channels 24/7. The moment a BUY or SELL signal drops, xceller. parses it — entry price, stop loss, and every TP level.',
                detail: 'Supports 50+ signal formats',
                side: 'left'
              },
              {
                num: '02',
                icon: Zap,
                title: 'Instant Execution',
                color: '#f2c978',
                glow: 'rgba(242,201,120,0.22)',
                desc: 'Each signal is validated against live market prices before a single order is placed. Lot size, risk percentage, and slippage — all handled automatically.',
                detail: 'Avg execution: 0.3s',
                side: 'right'
              },
              {
                num: '03',
                icon: TrendingUp,
                title: 'Live Tracking',
                color: '#7df9c6',
                glow: 'rgba(125,249,198,0.22)',
                desc: 'Every open position is tracked in real time. P&L updates tick by tick. When trades close, your history is logged and ready to export.',
                detail: 'Full Excel export included',
                side: 'left'
              },
            ];

            return (
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 0 }}>
                <TravellingOrb nodeRefs={nodeRefs} />

                {steps.map((step, i) => (
                  <FadeUp key={i} delay={i * 0.15}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 80px 1fr',
                        gap: 0,
                        alignItems: 'center',
                        marginBottom: i < 2 ? 40 : 0,
                      }}
                      className="steps-grid"
                    >
                      {step.side === 'left' ? (
                        <div
                          className="step-card-left"
                          style={{
                            background: isDark
                              ? 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 44%, rgba(255,255,255,0.01) 100%), rgba(255,255,255,0.02)'
                              : 'linear-gradient(160deg, rgba(255,255,255,0.84) 0%, rgba(255,255,255,0.62) 44%, rgba(255,255,255,0.56) 100%)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : t.sectionBorder}`,
                            borderRadius: 24,
                            padding: '28px 32px',
                            borderLeft: `3px solid ${step.color}`,
                            boxShadow: `0 20px 50px rgba(0,0,0,0.20), 0 0 44px ${step.glow}`,
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(22px) saturate(1.2)',
                            animation: `stepFadeLeft 0.65s cubic-bezier(0.22,1,0.36,1) both`,
                            animationDelay: `${0.1 + i * 0.15}s`,
                          }}
                        >
                          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: step.glow, filter: 'blur(30px)', pointerEvents: 'none' }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: `${step.color}18`, border: `1px solid ${step.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <step.icon size={18} color={step.color} />
                            </div>
                            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>{step.title}</h3>
                          </div>
                          <p style={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.7, margin: '0 0 14px' }}>{step.desc}</p>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${step.color}15`, borderRadius: 8, padding: '4px 10px' }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: step.color }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: step.color }}>{step.detail}</span>
                          </div>
                        </div>
                      ) : <div />}

                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2 }} className="step-center-col">
                        <div
                          ref={nodeRefs[i]}
                          className="node-shell"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
                            border: `2px solid ${step.color}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 0 20px ${step.glow}`,
                            backdropFilter: 'blur(8px)',
                            animation: `nodePopIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both`,
                            animationDelay: `${0.25 + i * 0.15}s`,
                            position: 'relative',
                            overflow: 'visible'
                          }}
                        >
                          <div
                            className="node-inner-ring"
                            style={{
                              position: 'absolute',
                              inset: -4,
                              borderRadius: '50%',
                              border: `1px solid ${step.color}55`,
                              pointerEvents: 'none'
                            }}
                          />
                          <span style={{ fontSize: 11, fontWeight: 900, color: step.color, letterSpacing: '0.05em', position: 'relative', zIndex: 1 }}>
                            {step.num}
                          </span>
                        </div>
                      </div>

                      {step.side === 'right' ? (
                        <div
                          className="step-card-right"
                          style={{
                            background: isDark
                              ? 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 44%, rgba(255,255,255,0.01) 100%), rgba(255,255,255,0.02)'
                              : 'linear-gradient(160deg, rgba(255,255,255,0.84) 0%, rgba(255,255,255,0.62) 44%, rgba(255,255,255,0.56) 100%)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : t.sectionBorder}`,
                            borderRadius: 24,
                            padding: '28px 32px',
                            borderRight: `3px solid ${step.color}`,
                            boxShadow: `0 20px 50px rgba(0,0,0,0.20), 0 0 44px ${step.glow}`,
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(22px) saturate(1.2)',
                            animation: `stepFadeRight 0.65s cubic-bezier(0.22,1,0.36,1) both`,
                            animationDelay: `${0.1 + i * 0.15}s`,
                          }}
                        >
                          <div style={{ position: 'absolute', top: -20, left: -20, width: 100, height: 100, borderRadius: '50%', background: step.glow, filter: 'blur(30px)', pointerEvents: 'none' }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: `${step.color}18`, border: `1px solid ${step.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <step.icon size={18} color={step.color} />
                            </div>
                            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>{step.title}</h3>
                          </div>
                          <p style={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.7, margin: '0 0 14px' }}>{step.desc}</p>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${step.color}15`, borderRadius: 8, padding: '4px 10px' }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: step.color }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: step.color }}>{step.detail}</span>
                          </div>
                        </div>
                      ) : <div />}
                    </div>
                  </FadeUp>
                ))}
              </div>
            );
          })()}
        </section>

        {/* Trust cards */}
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px 72px' }}>
          <StaggerContainer stagger={0.1} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="trust-grid">
            {[
              {
                icon: Shield, label: 'Secure by default', sub: 'Hashed credentials · encrypted traffic',
                color: '#8fb7ff', glow: 'rgba(143,183,255,0.15)',
                stat: '256-bit', statLabel: 'AES encryption',
                bar: 100,
              },
              {
                icon: Clock, label: 'Sub-second execution', sub: 'Signal to live trade in under 1 second',
                color: '#f2c978', glow: 'rgba(242,201,120,0.15)',
                stat: '0.3s', statLabel: 'avg latency',
                bar: 97,
              },
              {
                icon: BarChart3, label: 'Real-time dashboard', sub: 'Live P&L tracking across all positions',
                color: '#7df9c6', glow: 'rgba(125,249,198,0.15)',
                stat: '100%', statLabel: 'uptime SLA',
                bar: 100,
              },
            ].map((item, i) => (
              <StaggerItem key={i} y={20}>
                <div
                  className="trust-big-card prism-panel iri-border"
                  style={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 24,
                    padding: '28px 28px 24px',
                    background: isDark
                      ? `linear-gradient(150deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 42%, rgba(255,255,255,0.01) 100%), linear-gradient(145deg, ${item.glow} 0%, rgba(255,255,255,0.02) 100%)`
                      : `linear-gradient(150deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.60) 42%, rgba(255,255,255,0.56) 100%), linear-gradient(145deg, ${item.glow} 0%, rgba(255,255,255,0.30) 100%)`,
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : `${item.color}20`}`,
                    boxShadow: `0 12px 36px rgba(0,0,0,0.18), 0 4px 32px ${item.glow}`,
                    animation: `trustSlide 0.55s cubic-bezier(0.22,1,0.36,1) both`,
                    animationDelay: `${i * 0.1}s`,
                    cursor: 'default',
                    backdropFilter: 'blur(24px) saturate(1.18)',
                  }}
                >
                  <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${item.color}25 0%, transparent 70%)`, pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', top: 14, bottom: 14, right: 10, width: 4, borderRadius: 999, background: `linear-gradient(180deg, rgba(248,251,255,0.06) 0%, ${item.color} 50%, rgba(248,251,255,0.12) 100%)`, boxShadow: `0 0 18px ${item.color}40`, pointerEvents: 'none', opacity: 0.88 }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 13, flexShrink: 0,
                      background: `${item.color}15`, border: `1px solid ${item.color}35`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <item.icon size={20} color={item.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>{item.label}</div>
                      <div style={{ fontSize: 11.5, color: t.textMuted, marginTop: 1 }}>{item.sub}</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.04em', background: `linear-gradient(135deg, ${item.color}, #fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{item.stat}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, marginLeft: 8 }}>{item.statLabel}</span>
                  </div>

                  <div style={{ height: 3, background: `${item.color}15`, borderRadius: 99, overflow: 'hidden' }}>
                    <div
                      className="trust-bar-fill"
                      style={{
                        height: '100%',
                        width: `${item.bar}%`,
                        borderRadius: 99,
                        background: `linear-gradient(90deg, ${item.color}80, ${item.color})`,
                        boxShadow: `0 0 8px ${item.color}`,
                        color: item.color,
                      }}
                    />
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        {/* CTA */}
        <ScaleIn>
          <section style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px 80px' }}>
            <div
              className="cta-card prism-panel iri-border"
              style={{
                position: 'relative',
                borderRadius: 24,
                overflow: 'hidden',
                background: 'linear-gradient(140deg, rgba(8,10,16,0.92) 0%, rgba(9,14,21,0.88) 40%, rgba(6,11,18,0.96) 100%), linear-gradient(135deg, rgba(143,183,255,0.12) 0%, rgba(125,249,198,0.08) 46%, rgba(242,201,120,0.09) 100%)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 0 0 1px rgba(255,255,255,0.03) inset, 0 32px 80px rgba(0,0,0,0.34), 0 12px 48px rgba(111,168,255,0.12)',
              }}
            >
              <div style={{ position: 'absolute', top: '-40%', left: '-10%', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(143,183,255,0.18) 0%, transparent 65%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: '-40%', right: '-5%', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(125,249,198,0.14) 0%, transparent 65%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: 24, bottom: 24, right: 18, width: 5, borderRadius: 999, background: 'linear-gradient(180deg, rgba(248,251,255,0.08) 0%, rgba(143,183,255,0.78) 24%, rgba(125,249,198,0.76) 56%, rgba(242,201,120,0.72) 84%, rgba(248,251,255,0.12) 100%)', boxShadow: '0 0 24px rgba(143,183,255,0.22)', pointerEvents: 'none' }} />

              <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.035,
                backgroundImage: 'repeating-linear-gradient(135deg, rgba(143,183,255,0.9) 0px, rgba(143,183,255,0.9) 1px, transparent 1px, transparent 32px)',
              }} />
              <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '35%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(143,183,255,0.18) 40%, rgba(143,183,255,0.35) 50%, rgba(143,183,255,0.18) 60%, transparent)',
                  animation: 'diagShimmer 4s ease-in-out infinite',
                  animationDelay: '0s',
                }} />
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '20%', height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(125,249,198,0.1) 40%, rgba(125,249,198,0.22) 50%, rgba(125,249,198,0.1) 60%, transparent)',
                  animation: 'diagShimmer2 4s ease-in-out infinite',
                  animationDelay: '1.6s',
                }} />
              </div>

              <div style={{ position: 'relative', zIndex: 1, padding: '52px 52px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 48, alignItems: 'center' }} className="cta-inner-grid">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                    <div style={{ height: 1, width: 28, background: 'linear-gradient(90deg, #8fb7ff, transparent)' }} />
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8fb7ff' }}>Get started</span>
                  </div>
                  <h2 style={{ fontFamily: displayFont, fontSize: 40, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.12, margin: '0 0 16px', maxWidth: 520 }}>
                    Automate your trades.<br />
                    <span style={{ background: 'linear-gradient(90deg, #f8fbff 0%, #8fb7ff 24%, #7df9c6 58%, #f2c978 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      Start in under 2 minutes.
                    </span>
                  </h2>
                  <p style={{ fontSize: 14.5, color: t.textMuted, lineHeight: 1.65, margin: 0, maxWidth: 440 }}>
                    Connect your Telegram channel, set your lot size, and xceller. handles the rest — every signal parsed and placed in real time.
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', flexShrink: 0 }}>
                  <Link
                    to="/login"
                    className="cta-button"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 10,
                      padding: '16px 40px', borderRadius: 14,
                      background: iri,
                      color: '#fff', fontSize: 15, fontWeight: 800,
                      textDecoration: 'none',
                      boxShadow: '0 16px 36px rgba(111,168,255,0.24), inset 0 1px 0 rgba(255,255,255,0.15)',
                      letterSpacing: '-0.01em', whiteSpace: 'nowrap',
                    }}
                  >
                    Get Started Free <ArrowRight size={16} />
                  </Link>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                    {[
                      { icon: Shield, txt: 'No credit card' },
                      { icon: Zap, txt: 'Instant setup' },
                      { icon: TrendingUp, txt: 'Cancel anytime' },
                    ].map(({ icon: Ic, txt }, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Ic size={11} color="rgba(143,183,255,0.72)" />
                        <span style={{ fontSize: 11.5, fontWeight: 500, color: 'rgba(143,183,255,0.72)' }}>{txt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </ScaleIn>

        <Footer />
      </div>
    </>
  );
}
